import React from 'react';
import { useMiAsistencia } from 'hooks/useMiAsistencia';
import {
  EstadoAsistencia,
  ESTADO_LABEL,
  ESTADO_COLOR,
  ESTADO_DOT,
  ESTADOS,
} from 'types/asistencia.types';

// ============================================================
// MiAsistenciaPage – Vista de asistencia del estudiante
// ============================================================

const SpinnerIcon = () => (
  <svg className="animate-spin w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

const ESTADO_ICON: Record<EstadoAsistencia, React.ReactNode> = {
  PRESENTE: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
      <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd" />
    </svg>
  ),
  AUSENTE: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
      <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z" clipRule="evenodd" />
    </svg>
  ),
  TARDANZA: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
      <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z" clipRule="evenodd" />
    </svg>
  ),
  JUSTIFICADO: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
      <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
    </svg>
  ),
};

const MiAsistenciaPage: React.FC = () => {
  const {
    anios, cursos,
    asignacionId, setAsignacionId,
    registros, resumen,
    loading, loadingData, error,
    filtroEstado, setFiltroEstado,
  } = useMiAsistencia();

  const porcentajeColor = resumen.porcentaje >= 70
    ? 'text-green-600'
    : resumen.porcentaje >= 50
      ? 'text-amber-600'
      : 'text-red-600';

  const cursoSeleccionado = cursos.find(c => c.asignacionAcademicaId === asignacionId);

  return (
    <div className="space-y-6">

      {/* Cabecera */}
      <div>
        <h2 className="text-xl font-bold text-slate-800">Mi Asistencia</h2>
        <p className="text-slate-400 text-sm mt-0.5">Historial de asistencias por curso y año escolar</p>
      </div>

      {/* Año activo + selector de curso */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Año escolar activo (único) — solo se muestra como etiqueta */}
        {anios[0] && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border
                           border-blue-200 bg-blue-50 text-blue-700 text-sm font-semibold">
            Año escolar {anios[0].anio}
          </span>
        )}

        {/* Curso / asignación */}
        <select
          value={asignacionId ?? ''}
          onChange={e => setAsignacionId(Number(e.target.value))}
          disabled={cursos.length === 0}
          className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white
                     focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none
                     disabled:opacity-50 min-w-[200px]"
        >
          {cursos.length === 0 && <option value="">Sin cursos matriculados</option>}
          {cursos.map(c => (
            <option key={c.asignacionAcademicaId} value={c.asignacionAcademicaId}>
              {c.cursoNombre}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600 text-sm">{error}</div>
      )}

      {loading && registros.length === 0 ? (
        <div className="flex justify-center py-20"><SpinnerIcon /></div>
      ) : (
        <>
          {/* Docente del curso seleccionado */}
          {cursoSeleccionado && (
            <p className="text-sm text-slate-500">
              Docente:{' '}
              <span className="font-medium text-slate-700">
                {cursoSeleccionado.docenteNombres} {cursoSeleccionado.docenteApellidos}
              </span>
            </p>
          )}

          {/* Cards de resumen */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Porcentaje general */}
            <div className="col-span-2 sm:col-span-3 lg:col-span-1 bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
              {loadingData ? (
                <div className="flex justify-center py-2"><SpinnerIcon /></div>
              ) : (
                <>
                  <p className={`text-3xl font-bold ${porcentajeColor}`}>{resumen.porcentaje}%</p>
                  <p className="text-slate-400 text-sm mt-1">Asistencia</p>
                  <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        resumen.porcentaje >= 70 ? 'bg-green-500' : resumen.porcentaje >= 50 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${resumen.porcentaje}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{resumen.total} clases</p>
                </>
              )}
            </div>

            {/* Contadores por estado */}
            {ESTADOS.map(e => (
              <div key={e} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${ESTADO_COLOR[e]}`}>
                  {ESTADO_ICON[e]}
                </div>
                <p className="text-2xl font-bold text-slate-800">
                  {loadingData ? '—' : resumen[e.toLowerCase() as keyof typeof resumen] as number}
                </p>
                <p className="text-slate-400 text-sm">{ESTADO_LABEL[e]}</p>
              </div>
            ))}
          </div>

          {/* Filtro por estado */}
          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={() => setFiltroEstado('')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                ${filtroEstado === ''
                  ? 'bg-slate-800 text-white'
                  : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
                }`}
            >
              Todos
            </button>
            {ESTADOS.map(e => (
              <button
                key={e}
                onClick={() => setFiltroEstado(filtroEstado === e ? '' : e)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all
                  ${filtroEstado === e
                    ? `${ESTADO_COLOR[e]} shadow-sm`
                    : 'bg-white text-slate-400 border-slate-200 hover:bg-slate-50'
                  }`}
              >
                <span className={`w-2 h-2 rounded-full ${filtroEstado === e ? ESTADO_DOT[e] : 'bg-slate-300'}`} />
                {ESTADO_LABEL[e]}
              </button>
            ))}
            {filtroEstado && (
              <span className="text-xs text-slate-400">{registros.length} resultado{registros.length !== 1 ? 's' : ''}</span>
            )}
          </div>

          {/* Tabla */}
          {loadingData ? (
            <div className="flex justify-center py-12"><SpinnerIcon /></div>
          ) : registros.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-12 text-center">
              <p className="text-slate-400 text-sm">No se encontraron registros para este curso</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Fecha</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wide">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Motivo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {registros
                    .slice()
                    .sort((a, b) => b.fecha.localeCompare(a.fecha))
                    .map(r => (
                      <tr key={r.idAsistencia} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-slate-700 font-medium">{r.fecha}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border ${ESTADO_COLOR[r.estado]}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${ESTADO_DOT[r.estado]}`} />
                            {ESTADO_LABEL[r.estado]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {r.motivo || <span className="text-slate-300">—</span>}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MiAsistenciaPage;
